/* ============================================================
   ctr-gift-guard — keeps free gifts tied to the product that earns
   them.

   Every gift line carries `_gift` = the variant id of the paid
   product it came with. When that product leaves the cart its gifts
   go with it, and a cart holding nothing but gifts cannot reach
   checkout. Gifts are ordinary line items priced to zero by a
   discount code or a Function Studio flow, so without this the
   discount survives the paid product and the gifts stay free.

   Loaded from theme.liquid, not from a section: the cart is editable
   on every page, and the add-to-cart scripts only run on their own.
   ============================================================ */

(function () {
  'use strict';

  var GIFT_PROP = '_gift';
  var CHECKOUT_SELECTOR = '.cd__checkout-btn, [name="checkout"]';
  var BLOCKED_CLASS = 'ctr-gift-blocked';
  var CART_MUTATION = /\/cart\/(change|update)(\.js)?(\?|$)/;

  var lastCart = null;
  var lastCartAt = 0;
  var running = false;

  // How long a cached cart may back a request rewrite. Long enough to cover a
  // shopper's next click, short enough that another tab's changes can't drive it.
  var CACHE_TTL_MS = 30000;

  function giftOwner(item) {
    var owner = item.properties && item.properties[GIFT_PROP];
    return owner ? Number(owner) : null;
  }

  function isGift(item) {
    return giftOwner(item) !== null;
  }

  // The ShipInsure fee line (ctr-cart-drawer stamps `_protection`) is not a
  // product: it never keeps gifts alive and never unlocks checkout by itself.
  function isProtection(item) {
    return Boolean(item.properties && item.properties._protection);
  }

  // Housekeeping that must not sit on a path the shopper is waiting for.
  function whenIdle(fn) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(function () { fn(); }, { timeout: 2000 });
    } else {
      setTimeout(fn, 0);
    }
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); });
  }

  // A gift is orphaned once the variant that earned it is gone. Matching on
  // variant id rather than "any paid line" keeps a second, unrelated product
  // in the cart from holding someone else's gifts alive.
  function orphanGifts(cart) {
    var paid = cart.items
      .filter(function (item) { return !isGift(item) && !isProtection(item); })
      .map(function (item) { return Number(item.variant_id); });

    return cart.items.filter(function (item) {
      var owner = giftOwner(item);
      return owner !== null && paid.indexOf(owner) === -1;
    });
  }

  function hasPaid(items) {
    return items.some(function (item) { return !isGift(item) && !isProtection(item); });
  }

  function giftsOnly(cart) {
    return Boolean(cart && cart.items && cart.items.length && !hasPaid(cart.items));
  }

  function injectStyle() {
    if (!document.head || document.getElementById('ctr-gift-guard-style')) return;
    var style = document.createElement('style');
    style.id = 'ctr-gift-guard-style';
    style.textContent = '.' + BLOCKED_CLASS + '{opacity:.5;cursor:not-allowed}';
    document.head.appendChild(style);
  }

  function setCheckoutBlocked(blocked) {
    if (blocked) injectStyle();
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (el) {
      el.classList.toggle(BLOCKED_CLASS, blocked);
      if (blocked) {
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('aria-disabled');
      }
    });
  }

  function refreshDrawer() {
    if (typeof window.cart_build === 'function') window.cart_build();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // Gifts are free because of a cart-wide discount code or an `_offer`
  // attribute. Both outlive the lines they priced, so clear them once the
  // last gift is gone and nothing paid is left to justify them.
  function clearGiftPricing() {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '', attributes: { _offer: '' } })
    }).catch(function () {});
  }

  function removeLines(lines) {
    var updates = {};
    lines.forEach(function (item) { updates[item.key] = 0; });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (res) { return res.json(); });
  }

  // Line keys a cart request is about to zero out. Covers both shapes the theme
  // uses: /cart/change.js {id, quantity} and /cart/update.js {updates}.
  function zeroedKeys(body) {
    if (!body) return [];
    if (body.updates) {
      return Object.keys(body.updates).filter(function (k) { return Number(body.updates[k]) === 0; });
    }
    if (Number(body.quantity) === 0 && body.id != null) return [String(body.id)];
    return [];
  }

  // Rewrites a removal into one /cart/update.js that also drops the gifts the
  // removed line was earning. Returns null when there is nothing to fold in, so
  // ordinary removals pass through untouched.
  function foldGiftsInto(body) {
    if (!lastCart || !lastCart.items) return null;
    if (Date.now() - lastCartAt > CACHE_TTL_MS) return null;

    // The caller asked for rendered sections back (Dawn's cart page does, and
    // then reads them out of the response). Rewriting the request would change
    // what it gets, so leave those alone and let the slower sweep handle them.
    if (body && (body.sections || body.sections_url)) return null;

    var zeroed = zeroedKeys(body);
    if (!zeroed.length) return null;

    var leaving = lastCart.items.filter(function (item) {
      return zeroed.indexOf(String(item.key)) !== -1 || zeroed.indexOf(String(item.variant_id)) !== -1;
    });
    if (!leaving.length) return null;

    var leavingVariants = leaving.map(function (item) { return Number(item.variant_id); });
    var doomed = lastCart.items.filter(function (item) {
      var owner = giftOwner(item);
      return owner !== null && leavingVariants.indexOf(owner) !== -1;
    });
    if (!doomed.length) return null;

    var going = leaving.concat(doomed);
    var updates = {};
    going.forEach(function (item) { updates[item.key] = 0; });

    var staying = lastCart.items.filter(function (item) { return going.indexOf(item) === -1; });

    var payload = { updates: updates };
    if (!hasPaid(staying)) {
      payload.discount = '';
      payload.attributes = { _offer: '' };
    }
    return payload;
  }

  // `known` is the cart body a /cart/change|update call just returned. Using it
  // saves a /cart.js round trip on the path the shopper actually waits for, and
  // a cart with no gift lines costs nothing at all.
  function enforce(known) {
    if (running) return Promise.resolve();

    var given = known && known.items ? known : null;

    if (given && !given.items.some(isGift)) {
      lastCart = given;
      lastCartAt = Date.now();
      setCheckoutBlocked(false);
      return Promise.resolve();
    }

    running = true;

    return Promise.resolve(given || getCart())
      .then(function (cart) {
        lastCart = cart;
        lastCartAt = Date.now();
        var orphans = orphanGifts(cart);

        if (orphans.length === 0) {
          setCheckoutBlocked(giftsOnly(cart));
          return null;
        }

        return removeLines(orphans)
          .then(function (updated) {
            lastCart = updated;
            lastCartAt = Date.now();
            return hasPaid(updated.items) ? null : clearGiftPricing();
          })
          .then(function () {
            setCheckoutBlocked(false);
            refreshDrawer();
          });
      })
      .catch(function (err) {
        console.error('[gift-guard]', err);
      })
      .finally(function () {
        running = false;
      });
  }

  var passingThrough = false;

  function letThrough(trigger) {
    passingThrough = true;
    trigger.click();
    setTimeout(function () { passingThrough = false; }, 2000);
  }

  // Checkout must not be reachable with nothing but gifts. The cached cart is
  // never enough to decide this: it can be recent and still wrong, because an
  // add, another tab or a stale page changed the cart without telling us. So
  // the cart is read every time — one request on a rare click, in exchange for
  // the guarantee this whole script exists to give.
  document.addEventListener('click', function (e) {
    if (passingThrough) return;

    var trigger = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    getCart()
      .then(function (cart) {
        lastCart = cart;
        lastCartAt = Date.now();
        if (giftsOnly(cart)) {
          setCheckoutBlocked(true);
          return enforce(cart);
        }
        letThrough(trigger);
      })
      // A failed read must not strand a shopper with a legitimate cart.
      .catch(function () { letThrough(trigger); });
  }, true);

  var guardFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
    var opts = arguments[1];

    if (CART_MUTATION.test(urlStr) && opts && typeof opts.body === 'string') {
      var folded = null;
      try { folded = foldGiftsInto(JSON.parse(opts.body)); } catch (e) { /* not JSON */ }

      if (folded) {
        var origArgs = arguments;
        var merged = guardFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(folded)
        })
          .then(function (res) {
            // A stale line key would fail the whole removal, so hand the original
            // request through instead of leaving the cart untouched.
            if (!res.ok) return guardFetch.apply(window, origArgs);
            try { res.clone().json().then(enforce, function () {}); } catch (e) { /* body gone */ }
            return res;
          })
          // Whatever goes wrong with the rewrite is ours, not the caller's: it
          // asked to remove a line and must still get that request's outcome.
          .catch(function () { return guardFetch.apply(window, origArgs); });
        return merged;
      }
    }

    var result = guardFetch.apply(this, arguments);

    if (CART_MUTATION.test(urlStr)) {
      result
        .then(function (res) {
          if (!res || typeof res.clone !== 'function') return enforce();
          return res.clone().json().then(enforce, function () { return enforce(); });
        })
        .catch(function () {});
    }

    return result;
  };

  var guardOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('load', function () {
      if (!CART_MUTATION.test(url)) return;
      var body = null;
      try { body = JSON.parse(this.responseText); } catch (e) { /* not JSON */ }
      enforce(body);
    });
    return guardOpen.apply(this, arguments);
  };

  // Exposed for the cart-state checks and for debugging a live cart from the
  // console: window.ctrGiftGuard.enforce().
  window.ctrGiftGuard = { orphanGifts: orphanGifts, giftsOnly: giftsOnly, enforce: enforce, foldGiftsInto: foldGiftsInto };

  // The opening pass is housekeeping, not part of rendering the page: let the
  // cart drawer build first so this never delays what the shopper sees.
  whenIdle(function () { enforce(); });
})();
